import type {
  CellSamples,
  RateGrid,
  SampleShipment,
} from "@redotech/admin-sdk/rpc/schema/rate-tables/parse-carrier-invoice-csv";
import {
  calculateDhlPickupCostPerPackage,
  calculateDiscountQuote,
  calculateFlatDiscountQuote,
  calculateSpreadPercentQuote,
  calculateSpreadQuote,
  type DhlSettings,
  getDhlAnnualPickupCost,
  getRevSharePercentage,
  lookupRateByIndex,
  OZ_PER_LB,
  PricingStrategy,
  REV_SHARE_CARRIERS,
  type RevShareSettings,
  SpreadUnit,
  WEEKDAYS_PER_YEAR,
} from "@redotech/redo-model/outbound-labels/pricing-calculator";
import type { RedoCarrierRateTables } from "@redotech/redo-model/rate-table/global-rate-tables";
import { WeightUnit } from "@redotech/redo-model/rate-table/rate-table";
import { RateTableCarrier } from "@redotech/redo-model/rate-table/rate-table-mapping";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import {
  type CarrierEligibility,
  type DataSource,
  type MissingMerchantRateMode,
  type RateCalcCarrier,
} from "../types";
import {
  calculateInterpolatedSpread,
  formatWeightOz,
  getBaselineTableForCarrier,
} from "../utils/pricing-calculator";
import { CellDetailModal } from "./cell-detail-modal";
import * as styles from "./comparison-rate-table.module.css";
import { OrderDistributionTable } from "./order-distribution-table";

export enum ViewMode {
  Merchant = "Merchant Current Rate",
  Redo = "Carrier Rate",
  Quoted = "Merchant Rate Card",
  Savings = "Savings",
  Spread = "Spread",
  Orders = "Orders",
  Distribution = "Distribution",
}

interface ComparisonRateTableProps {
  rateGrid: RateGrid;
  rateTables: RedoCarrierRateTables;
  selectedCarrier: RateCalcCarrier;
  carrierEligibility: CarrierEligibility;
  pricingStrategy: PricingStrategy;
  unit: SpreadUnit;
  amount: number;
  missingMerchantRateMode: MissingMerchantRateMode;
  defaultSpreadNoMerchantRate: number;
  defaultSpreadUnit: SpreadUnit;
  dhlSettings: DhlSettings;
  dailyVolume: number;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  revShareSettings: RevShareSettings;
  dataSource: DataSource;
  exportRef?: React.MutableRefObject<(() => void) | null>;
}

interface AggregatedCell {
  weightBucket: { minWeight: number; maxWeight: number };
  zone: string;
  merchantRate: number | null;
  carrierRate: number | null;
  quotedRate: number | null;
  savings: number | null;
  spread: number | null;
  orderCount: number;
  samples: CellSamples | null;
}

interface SelectedCellInfo {
  label: string;
  samples: CellSamples | null;
  orderCount: number;
}

function convertWeight(weightOz: number, targetUnit: WeightUnit): number {
  return targetUnit === WeightUnit.LB ? weightOz / OZ_PER_LB : weightOz;
}

function findWeightBucketIndex(
  weightOz: number,
  buckets: { minWeight: number; maxWeight: number }[],
  unit: WeightUnit,
): number {
  const weight = convertWeight(weightOz, unit);
  return buckets.findIndex(
    (b) => weight > b.minWeight && weight <= b.maxWeight,
  );
}

const MAX_AGGREGATED_SAMPLES = 20;

function calculateMode(arr: number[]): number {
  if (arr.length === 0) {
    return 0;
  }
  const counts = new Map<number, number>();
  for (const v of arr) {
    counts.set(v, (counts.get(v) || 0) + 1);
  }
  const best = [...counts.entries()].reduce(
    (b, [v, c]) => (c > b[1] ? [v, c] : b),
    [arr[0]!, 0] as [number, number],
  );
  if (best[1] <= 1) {
    return Math.min(...arr);
  }
  return best[0];
}

function buildAggregatedSamples(
  samples: SampleShipment[],
  prices: number[],
): CellSamples | null {
  if (prices.length === 0) {
    return null;
  }

  const sorted = [...prices].sort((a, b) => a - b);
  const min = sorted[0]!;
  const max = sorted[sorted.length - 1]!;
  const sum = prices.reduce((a, b) => a + b, 0);
  const avg = sum / prices.length;
  const median =
    sorted.length % 2 === 0
      ? (sorted[sorted.length / 2 - 1]! + sorted[sorted.length / 2]!) / 2
      : sorted[Math.floor(sorted.length / 2)]!;
  const modeVal = calculateMode(prices);
  const variance =
    prices.reduce((acc, p) => acc + (p - avg) ** 2, 0) / prices.length;
  const stdDev = Math.sqrt(variance);

  const sampleSubset =
    samples.length <= MAX_AGGREGATED_SAMPLES
      ? samples
      : [...samples]
          .sort(() => Math.random() - 0.5)
          .slice(0, MAX_AGGREGATED_SAMPLES);

  const margins = sampleSubset
    .map((s) => s.currentRedoMargin)
    .filter((m): m is number => m != null);
  const marginStats =
    margins.length > 0
      ? {
          min: Math.min(...margins),
          max: Math.max(...margins),
          avg:
            Math.round(
              (margins.reduce((a, b) => a + b, 0) / margins.length) * 100,
            ) / 100,
          median:
            margins.length % 2 === 0
              ? ([...margins].sort((a, b) => a - b)[margins.length / 2 - 1]! +
                  [...margins].sort((a, b) => a - b)[margins.length / 2]!) /
                2
              : [...margins].sort((a, b) => a - b)[
                  Math.floor(margins.length / 2)
                ]!,
          count: margins.length,
        }
      : null;

  return {
    samples: sampleSubset,
    stats: {
      min,
      max,
      avg: Math.round(avg * 100) / 100,
      median,
      mode: modeVal,
      stdDev: Math.round(stdDev * 100) / 100,
    },
    marginStats,
  };
}

function getHeatColor(
  value: number,
  min: number,
  max: number,
  mode: ViewMode,
): string {
  if (max === min) {
    switch (mode) {
      case ViewMode.Merchant:
      case ViewMode.Redo:
      case ViewMode.Quoted:
        return "hsl(60, 55%, 82%)"; // Yellow-ish for uniform price values
      case ViewMode.Savings:
        return value <= 0 ? "hsl(0, 60%, 85%)" : "hsl(80, 55%, 80%)";
      case ViewMode.Spread:
        return value < 0 ? "hsl(0, 60%, 85%)" : "hsl(80, 55%, 80%)";
      case ViewMode.Orders:
        return "hsl(210, 55%, 82%)";
      default:
        return "#e5e7eb"; // gray-200 equivalent
    }
  }

  const ratio = Math.max(0, Math.min(1, (value - min) / (max - min)));

  switch (mode) {
    case ViewMode.Merchant:
    case ViewMode.Redo:
    case ViewMode.Quoted: {
      const priceHue = 120 - ratio * 120;
      return `hsl(${priceHue}, 55%, 82%)`;
    }
    case ViewMode.Savings: {
      if (value <= 0) {
        return "hsl(0, 60%, 85%)";
      }
      const savingsHue = 40 + ratio * 80;
      return `hsl(${savingsHue}, 55%, 80%)`;
    }
    case ViewMode.Spread: {
      if (value < 0) {
        return "hsl(0, 60%, 85%)";
      }
      if (value === 0) {
        return "hsl(40, 50%, 85%)";
      }
      const spreadHue = 40 + ratio * 80;
      return `hsl(${spreadHue}, 55%, 80%)`;
    }
    case ViewMode.Orders: {
      const orderLight = 92 - ratio * 40;
      return `hsl(210, 55%, ${orderLight}%)`;
    }
    default:
      return "#e5e7eb"; // gray-200 equivalent
  }
}

function formatLargeNumber(value: number): string {
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toFixed(2);
}

function exportTableAsCSV(
  data: AggregatedCell[][],
  zones: string[],
  weightBuckets: { minWeight: number; maxWeight: number }[],
  weightUnit: WeightUnit,
  viewMode: ViewMode,
  selectedCarrier: string,
): void {
  const rows: string[][] = [];

  const header = ["Weight", ...zones.map((z) => `Zone ${z}`)];
  rows.push(header);

  for (const [i, datum] of data.entries()) {
    const row = datum!;
    const weightBucket = weightBuckets[i]!;
    const weightLabel = formatWeightBucket(weightBucket, weightUnit);

    const rowData = [weightLabel];
    for (const cell of row) {
      const value = getValueForMode(
        viewMode,
        cell.merchantRate,
        cell.carrierRate,
        cell.quotedRate,
        cell.savings,
        cell.spread,
        cell.orderCount,
      );
      rowData.push(value !== null ? value.toFixed(2) : "");
    }
    rows.push(rowData);
  }

  const csvContent = rows.map((r) => r.join(",")).join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `rate-card-${selectedCarrier}-${viewMode.replace(/\s+/g, "-").toLowerCase()}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

function formatCellValue(value: number | null, mode: ViewMode): string {
  if (value === null) {
    return "-";
  }

  switch (mode) {
    case ViewMode.Orders:
      return value.toLocaleString();
    case ViewMode.Savings:
    case ViewMode.Spread:
      if (value === 0) {
        return "$0.00";
      }
      if (value < 0) {
        return `-$${Math.abs(value).toFixed(2)}`;
      }
      return `$${value.toFixed(2)}`;
    default:
      return `$${value.toFixed(2)}`;
  }
}

function formatWeightBucket(
  bucket: { minWeight: number; maxWeight: number },
  unit: WeightUnit,
): string {
  const totalOz =
    unit === WeightUnit.LB ? bucket.maxWeight * OZ_PER_LB : bucket.maxWeight;
  return formatWeightOz(totalOz);
}

export const ComparisonRateTable = memo(function ComparisonRateTable({
  rateGrid,
  rateTables,
  selectedCarrier,
  carrierEligibility,
  pricingStrategy,
  unit,
  amount,
  missingMerchantRateMode,
  defaultSpreadNoMerchantRate,
  defaultSpreadUnit,
  dhlSettings,
  dailyVolume,
  viewMode,
  onViewModeChange,
  revShareSettings,
  dataSource,
  exportRef,
}: ComparisonRateTableProps) {
  const isRevShareCarrier = (
    REV_SHARE_CARRIERS as readonly RateCalcCarrier[]
  ).includes(selectedCarrier);

  const [useAllOrders, setUseAllOrders] = useState(false);
  const [selectedCell, setSelectedCell] = useState<SelectedCellInfo | null>(
    null,
  );

  const handleCellClick = useCallback(
    (
      cell: AggregatedCell,
      weightBucket: { minWeight: number; maxWeight: number },
      weightUnit: WeightUnit,
    ) => {
      if (cell.orderCount === 0) {
        return;
      }
      const label = `${formatWeightBucket(weightBucket, weightUnit)} / Zone ${cell.zone}`;
      setSelectedCell({
        label,
        samples: cell.samples,
        orderCount: cell.orderCount,
      });
    },
    [],
  );

  const handleCloseModal = useCallback(() => {
    setSelectedCell(null);
  }, []);

  const baselineTable = useMemo(
    () =>
      getBaselineTableForCarrier(
        rateTables,
        selectedCarrier,
        carrierEligibility,
      ),
    [rateTables, selectedCarrier, carrierEligibility],
  );

  const isUspsMarketRatesMode = dataSource === "usps_market_rates";

  const { aggregatedData, stats } = useMemo(() => {
    if (!baselineTable) {
      return { aggregatedData: [], stats: null };
    }

    const weightBuckets = baselineTable.yAxis.values;
    const weightUnit = baselineTable.yAxis.units;
    const zones = baselineTable.xAxis.values;

    const dhlPickupCost =
      selectedCarrier === RateTableCarrier.DHL_ECS &&
      carrierEligibility?.dhl?.eligible
        ? calculateDhlPickupCostPerPackage(
            getDhlAnnualPickupCost(
              carrierEligibility.dhl.nearestDC,
              carrierEligibility.dhl.distanceMiles,
              dhlSettings.needsGL,
              dhlSettings.weeklyPickups,
            ),
            dailyVolume,
            dhlSettings.weeklyPickups,
          )
        : 0;

    // Build agg map for merchant oz > 16 only (carrier-grain, used in phase 2).
    // oz ≤ 16 are emitted as individual rows in phase 1 and don't need aggregation.
    interface AggEntry {
      totalRate: number;
      orderCount: number;
      samples: SampleShipment[];
      prices: number[];
    }
    const agg = new Map<number, Map<number, AggEntry>>();
    for (let wi = 0; wi < weightBuckets.length; wi++) {
      agg.set(wi, new Map());
      for (let zi = 0; zi < zones.length; zi++) {
        agg
          .get(wi)!
          .set(zi, { totalRate: 0, orderCount: 0, samples: [], prices: [] });
      }
    }

    for (let mwi = 0; mwi < rateGrid.weightBucketsOz.length; mwi++) {
      const merchantWeightOz = rateGrid.weightBucketsOz[mwi]!;
      if (merchantWeightOz <= OZ_PER_LB) {
        continue;
      } // phase 1 handles oz ≤ 16

      const baselineWeightIdx = findWeightBucketIndex(
        merchantWeightOz,
        weightBuckets,
        weightUnit,
      );
      if (baselineWeightIdx < 0) {
        continue;
      }

      for (let mzi = 0; mzi < rateGrid.zones.length; mzi++) {
        const merchantZone = rateGrid.zones[mzi]!;
        const merchantRate = rateGrid.priceGrid[mwi]?.[mzi] ?? null;
        const orderCount = rateGrid.countGrid[mwi]?.[mzi] ?? 0;
        if (merchantRate === null || orderCount === 0) {
          continue;
        }
        const baselineZoneIdx = zones.indexOf(merchantZone);
        if (baselineZoneIdx < 0) {
          continue;
        }
        const cell = agg.get(baselineWeightIdx)!.get(baselineZoneIdx)!;
        cell.totalRate += merchantRate * orderCount;
        cell.orderCount += orderCount;
        const merchantSamples = rateGrid.sampleGrid?.[mwi]?.[mzi];
        if (merchantSamples?.samples) {
          cell.samples.push(...merchantSamples.samples);
        }
        for (let i = 0; i < orderCount; i++) {
          cell.prices.push(merchantRate);
        }
      }
    }

    // precomputedGrid for interpolation (carrier-grain, used for empty cells)
    const precomputedGrid: {
      merchantRate: number | null;
      baseRate: number | null;
    }[][] = [];
    for (let wi = 0; wi < weightBuckets.length; wi++) {
      const row: { merchantRate: number | null; baseRate: number | null }[] =
        [];
      for (let zi = 0; zi < zones.length; zi++) {
        const aggCell = agg.get(wi)!.get(zi)!;
        const merchantRate =
          aggCell.orderCount > 0
            ? aggCell.totalRate / aggCell.orderCount
            : null;
        let baseRate = lookupRateByIndex(baselineTable, wi, zi);
        if (baseRate !== null && selectedCarrier === RateTableCarrier.DHL_ECS) {
          baseRate += dhlPickupCost;
        }
        row.push({ merchantRate, baseRate });
      }
      precomputedGrid.push(row);
    }

    const cells: AggregatedCell[][] = [];
    const displayWeightBuckets: { minWeight: number; maxWeight: number }[] = [];
    let minVal = Infinity;
    let maxVal = -Infinity;
    let totalOrders = 0;
    let totalMerchantCost = 0;
    let totalSavings = 0;
    let totalSpread = 0;
    let ordersWon = 0;
    let totalDealSize = 0;
    let totalSpreadAllOrders = 0;
    let totalDealSizeAllOrders = 0;

    // ── Phase 1: individual 1oz rows for merchant oz ≤ 16 ───────────────────
    for (let mwi = 0; mwi < rateGrid.weightBucketsOz.length; mwi++) {
      const merchantWeightOz = rateGrid.weightBucketsOz[mwi]!;
      if (merchantWeightOz > OZ_PER_LB) {
        break;
      }

      const displayBucket: { minWeight: number; maxWeight: number } =
        weightUnit === WeightUnit.LB
          ? {
              minWeight: (merchantWeightOz - 1) / OZ_PER_LB,
              maxWeight: merchantWeightOz / OZ_PER_LB,
            }
          : { minWeight: merchantWeightOz - 1, maxWeight: merchantWeightOz };

      const carrierWi = findWeightBucketIndex(
        merchantWeightOz,
        weightBuckets,
        weightUnit,
      );

      const row: AggregatedCell[] = [];
      for (const [zi, zone_] of zones.entries()) {
        const zone = zone_!;
        const merchantZoneIdx = rateGrid.zones.indexOf(zone);

        const rawMerchantRate =
          merchantZoneIdx >= 0
            ? (rateGrid.priceGrid[mwi]?.[merchantZoneIdx] ?? null)
            : null;
        const orderCount =
          merchantZoneIdx >= 0
            ? (rateGrid.countGrid[mwi]?.[merchantZoneIdx] ?? 0)
            : 0;
        const sampleData =
          merchantZoneIdx >= 0
            ? (rateGrid.sampleGrid?.[mwi]?.[merchantZoneIdx] ?? null)
            : null;

        let carrierRate: number | null = null;
        if (carrierWi >= 0) {
          carrierRate = lookupRateByIndex(baselineTable, carrierWi, zi);
          if (
            carrierRate !== null &&
            selectedCarrier === RateTableCarrier.DHL_ECS
          ) {
            carrierRate += dhlPickupCost;
          }
        }

        if (orderCount === 0) {
          if (carrierRate !== null) {
            let quotedRateForEmpty: number;
            let spreadForEmpty: number;
            if (isRevShareCarrier) {
              quotedRateForEmpty = carrierRate;
              const revSharePct = getRevSharePercentage(
                selectedCarrier,
                merchantWeightOz,
                revShareSettings,
              );
              spreadForEmpty = carrierRate * revSharePct;
            } else if (pricingStrategy === PricingStrategy.Spread) {
              quotedRateForEmpty =
                unit === SpreadUnit.Percent
                  ? calculateSpreadPercentQuote(carrierRate, amount)
                  : calculateSpreadQuote(carrierRate, amount);
              spreadForEmpty = quotedRateForEmpty - carrierRate;
            } else {
              if (missingMerchantRateMode === "interpolate" && carrierWi >= 0) {
                spreadForEmpty = calculateInterpolatedSpread(
                  precomputedGrid,
                  carrierWi,
                  zi,
                  carrierRate,
                  amount,
                );
              } else if (defaultSpreadUnit === SpreadUnit.Percent) {
                spreadForEmpty =
                  (defaultSpreadNoMerchantRate / 100) * carrierRate;
              } else {
                spreadForEmpty = defaultSpreadNoMerchantRate;
              }
              quotedRateForEmpty = carrierRate + spreadForEmpty;
            }
            row.push({
              weightBucket: displayBucket,
              zone,
              merchantRate: null,
              carrierRate,
              quotedRate: quotedRateForEmpty,
              savings: null,
              spread: spreadForEmpty,
              orderCount: 0,
              samples: null,
            });
          } else {
            row.push({
              weightBucket: displayBucket,
              zone,
              merchantRate: null,
              carrierRate: null,
              quotedRate: null,
              savings: null,
              spread: null,
              orderCount: 0,
              samples: null,
            });
          }
          continue;
        }

        totalOrders += orderCount;

        if (carrierRate === null) {
          if (rawMerchantRate !== null) {
            totalMerchantCost += rawMerchantRate * orderCount;
          }
          row.push({
            weightBucket: displayBucket,
            zone,
            merchantRate: rawMerchantRate,
            carrierRate: null,
            quotedRate: null,
            savings: null,
            spread: null,
            orderCount,
            samples: sampleData,
          });
          continue;
        }

        const merchantRate = rawMerchantRate!;
        totalMerchantCost += merchantRate * orderCount;
        let quotedRate: number;
        let spread: number;

        if (isRevShareCarrier) {
          quotedRate = carrierRate;
          const revSharePct = getRevSharePercentage(
            selectedCarrier,
            merchantWeightOz,
            revShareSettings,
          );
          spread = carrierRate * revSharePct;
        } else if (pricingStrategy === PricingStrategy.Discount) {
          quotedRate =
            unit === SpreadUnit.Dollars
              ? calculateFlatDiscountQuote(merchantRate, amount, carrierRate)
              : calculateDiscountQuote(merchantRate, amount, carrierRate);
          spread = quotedRate - carrierRate;
        } else {
          quotedRate =
            unit === SpreadUnit.Percent
              ? calculateSpreadPercentQuote(carrierRate, amount)
              : calculateSpreadQuote(carrierRate, amount);
          spread = quotedRate - carrierRate;
        }

        const savings = merchantRate - quotedRate;
        const won = savings > 0;

        totalSpreadAllOrders += spread * orderCount;
        if (spread > 0) {
          totalDealSizeAllOrders += spread * orderCount;
        }

        if (won) {
          ordersWon += orderCount;
          totalSavings += savings * orderCount;
          totalSpread += spread * orderCount;
          totalDealSize += quotedRate * orderCount;
        }

        const value = getValueForMode(
          viewMode,
          merchantRate,
          carrierRate,
          quotedRate,
          savings,
          spread,
          orderCount,
        );
        if (value !== null && value !== 0) {
          minVal = Math.min(minVal, value);
          maxVal = Math.max(maxVal, value);
        }

        row.push({
          weightBucket: displayBucket,
          zone,
          merchantRate,
          carrierRate,
          quotedRate,
          savings,
          spread,
          orderCount,
          samples: sampleData,
        });
      }
      cells.push(row);
      displayWeightBuckets.push(displayBucket);
    }

    // ── Phase 2: carrier-grain rows for buckets where maxWeightOz > 16 ──────
    for (const [wi, weightBucket_] of weightBuckets.entries()) {
      const weightBucket = weightBucket_!;
      const maxWeightOz =
        weightUnit === WeightUnit.LB
          ? weightBucket.maxWeight * OZ_PER_LB
          : weightBucket.maxWeight;
      if (maxWeightOz <= OZ_PER_LB) {
        continue;
      } // already covered by phase 1

      const row: AggregatedCell[] = [];

      for (const [zi, zone_] of zones.entries()) {
        const aggCell = agg.get(wi)!.get(zi)!;
        const zone = zone_!;

        if (aggCell.orderCount === 0) {
          let carrierRateForEmpty = lookupRateByIndex(baselineTable, wi, zi);
          if (
            carrierRateForEmpty !== null &&
            selectedCarrier === RateTableCarrier.DHL_ECS
          ) {
            carrierRateForEmpty += dhlPickupCost;
          }
          if (carrierRateForEmpty !== null) {
            let quotedRateForEmpty: number;
            let spreadForEmpty: number;

            if (isRevShareCarrier) {
              quotedRateForEmpty = carrierRateForEmpty;
              const weightOz =
                baselineTable.yAxis.units === WeightUnit.LB
                  ? weightBucket.maxWeight * OZ_PER_LB
                  : weightBucket.maxWeight;
              const revSharePct = getRevSharePercentage(
                selectedCarrier,
                weightOz,
                revShareSettings,
              );
              spreadForEmpty = carrierRateForEmpty * revSharePct;
            } else if (pricingStrategy === PricingStrategy.Spread) {
              quotedRateForEmpty =
                unit === SpreadUnit.Percent
                  ? calculateSpreadPercentQuote(carrierRateForEmpty, amount)
                  : calculateSpreadQuote(carrierRateForEmpty, amount);
              spreadForEmpty = quotedRateForEmpty - carrierRateForEmpty;
            } else {
              if (missingMerchantRateMode === "interpolate") {
                spreadForEmpty = calculateInterpolatedSpread(
                  precomputedGrid,
                  wi,
                  zi,
                  carrierRateForEmpty,
                  amount,
                );
              } else if (defaultSpreadUnit === SpreadUnit.Percent) {
                spreadForEmpty =
                  (defaultSpreadNoMerchantRate / 100) * carrierRateForEmpty;
              } else {
                spreadForEmpty = defaultSpreadNoMerchantRate;
              }
              quotedRateForEmpty = carrierRateForEmpty + spreadForEmpty;
            }

            row.push({
              weightBucket,
              zone,
              merchantRate: null,
              carrierRate: carrierRateForEmpty,
              quotedRate: quotedRateForEmpty,
              savings: null,
              spread: spreadForEmpty,
              orderCount: 0,
              samples: null,
            });
            continue;
          }
          row.push({
            weightBucket,
            zone,
            merchantRate: null,
            carrierRate: null,
            quotedRate: null,
            savings: null,
            spread: null,
            orderCount: 0,
            samples: null,
          });
          continue;
        }

        const aggregatedSamples = buildAggregatedSamples(
          aggCell.samples,
          aggCell.prices,
        );
        totalOrders += aggCell.orderCount;

        const merchantRate = aggCell.totalRate / aggCell.orderCount;
        totalMerchantCost += merchantRate * aggCell.orderCount;
        let carrierRate = lookupRateByIndex(baselineTable, wi, zi);
        if (
          carrierRate !== null &&
          selectedCarrier === RateTableCarrier.DHL_ECS
        ) {
          carrierRate += dhlPickupCost;
        }

        if (carrierRate === null) {
          row.push({
            weightBucket,
            zone,
            merchantRate,
            carrierRate: null,
            quotedRate: null,
            savings: null,
            spread: null,
            orderCount: aggCell.orderCount,
            samples: aggregatedSamples,
          });
          continue;
        }

        let quotedRate: number;
        let spread: number;

        if (isRevShareCarrier) {
          quotedRate = carrierRate;
          const weightOz =
            baselineTable.yAxis.units === WeightUnit.LB
              ? weightBucket.maxWeight * OZ_PER_LB
              : weightBucket.maxWeight;
          const revSharePct = getRevSharePercentage(
            selectedCarrier,
            weightOz,
            revShareSettings,
          );
          spread = carrierRate * revSharePct;
        } else {
          if (pricingStrategy === PricingStrategy.Discount) {
            quotedRate =
              unit === SpreadUnit.Dollars
                ? calculateFlatDiscountQuote(merchantRate, amount, carrierRate)
                : calculateDiscountQuote(merchantRate, amount, carrierRate);
          } else {
            quotedRate =
              unit === SpreadUnit.Percent
                ? calculateSpreadPercentQuote(carrierRate, amount)
                : calculateSpreadQuote(carrierRate, amount);
          }
          spread = quotedRate - carrierRate;
        }

        const savings = merchantRate - quotedRate;
        const won = savings > 0;

        totalSpreadAllOrders += spread * aggCell.orderCount;
        if (spread > 0) {
          totalDealSizeAllOrders += spread * aggCell.orderCount;
        }

        if (won) {
          ordersWon += aggCell.orderCount;
          totalSavings += savings * aggCell.orderCount;
          totalSpread += spread * aggCell.orderCount;
          totalDealSize += quotedRate * aggCell.orderCount;
        }

        const value = getValueForMode(
          viewMode,
          merchantRate,
          carrierRate,
          quotedRate,
          savings,
          spread,
          aggCell.orderCount,
        );
        if (value !== null && value !== 0) {
          minVal = Math.min(minVal, value);
          maxVal = Math.max(maxVal, value);
        }

        row.push({
          weightBucket,
          zone,
          merchantRate,
          carrierRate,
          quotedRate,
          savings,
          spread,
          orderCount: aggCell.orderCount,
          samples: aggregatedSamples,
        });
      }
      cells.push(row);
      displayWeightBuckets.push(weightBucket);
    }

    if (minVal === Infinity) {
      minVal = 0;
    }
    if (maxVal === -Infinity) {
      maxVal = 0;
    }

    const annualOrders = dailyVolume * WEEKDAYS_PER_YEAR;

    const scaleFactor = totalOrders > 0 ? annualOrders / totalOrders : 0;
    const annualSavings = totalSavings * scaleFactor;
    const annualSpread = totalSpread * scaleFactor;
    const annualDealSize = totalDealSize * scaleFactor;
    const avgOrderPrice = totalOrders > 0 ? totalMerchantCost / totalOrders : 0;
    const avgSpreadPerOrder = ordersWon > 0 ? totalSpread / ordersWon : 0;

    const annualSpreadAllOrders = totalSpreadAllOrders * scaleFactor;
    const annualDealSizeAllOrders = totalDealSizeAllOrders * scaleFactor;
    const avgSpreadPerOrderAllOrders =
      totalOrders > 0 ? totalSpreadAllOrders / totalOrders : 0;

    return {
      aggregatedData: cells,
      stats: {
        minVal,
        maxVal,
        totalOrders,
        totalSavings,
        totalSpread,
        ordersWon,
        totalDealSize,
        ordersWonPct: totalOrders > 0 ? (ordersWon / totalOrders) * 100 : 0,
        annualOrders,
        annualSavings,
        annualSpread,
        annualDealSize,
        avgOrderPrice,
        avgSpreadPerOrder,
        annualSpreadAllOrders,
        annualDealSizeAllOrders,
        avgSpreadPerOrderAllOrders,
        weightUnit: baselineTable.yAxis.units,
        zones: baselineTable.xAxis.values,
        weightBuckets: displayWeightBuckets,
      },
    };
  }, [
    baselineTable,
    rateGrid,
    selectedCarrier,
    carrierEligibility,
    pricingStrategy,
    unit,
    amount,
    missingMerchantRateMode,
    defaultSpreadNoMerchantRate,
    defaultSpreadUnit,
    dhlSettings,
    dailyVolume,
    viewMode,
    isRevShareCarrier,
    revShareSettings,
  ]);

  const allRows = useMemo(() => {
    if (!stats) {
      return [];
    }
    return aggregatedData.map((row, idx) => ({
      row,
      idx,
      weightBucket: stats.weightBuckets[idx]!,
    }));
  }, [aggregatedData, stats]);

  useEffect(() => {
    if (exportRef && stats) {
      exportRef.current = () =>
        exportTableAsCSV(
          aggregatedData,
          stats.zones,
          stats.weightBuckets,
          stats.weightUnit,
          viewMode,
          selectedCarrier,
        );
    }
  }, [exportRef, aggregatedData, stats, viewMode, selectedCarrier]);

  if (!baselineTable || !stats) {
    return (
      <Flex
        align="center"
        bgColor="secondary"
        borderColor="primary"
        borderWidth="1px"
        className={styles.emptyState}
        justify="center"
        p="xl"
        radius="md"
      >
        <Text textColor="tertiary">
          No rate table found for {selectedCarrier}
        </Text>
      </Flex>
    );
  }

  return (
    <Flex dir="column" gap="md">
      {!isUspsMarketRatesMode && (
        <Flex dir="column" gap="sm">
          <Flex gap="md" wrap="wrap">
            <StatCard
              label="Annual Volume"
              value={stats.annualOrders.toLocaleString()}
            />
            <StatCard
              label="Orders Won"
              textColor={stats.ordersWonPct > 50 ? "success" : "warning"}
              value={`${stats.ordersWonPct.toFixed(1)}%`}
            />
            <StatCard
              label="Annual Merchant Savings"
              textColor="success"
              value={`$${formatLargeNumber(stats.annualSavings)}`}
            />
            <StatCard
              label={
                isRevShareCarrier ? "Annual Rev Share" : "Annual Redo Spread"
              }
              textColor="primary"
              value={`$${formatLargeNumber(useAllOrders ? stats.annualSpreadAllOrders : stats.annualSpread)}`}
            />
            <StatCard
              label="Annual Deal Size"
              value={`$${formatLargeNumber(useAllOrders ? stats.annualDealSizeAllOrders : stats.annualDealSize)}`}
            />
            <StatCard
              label="Avg Order Price"
              value={`$${stats.avgOrderPrice.toFixed(2)}`}
            />
            <StatCard
              label={
                isRevShareCarrier ? "Avg Rev Share/Order" : "Avg Spread/Order"
              }
              textColor="primary"
              value={`$${(useAllOrders ? stats.avgSpreadPerOrderAllOrders : stats.avgSpreadPerOrder).toFixed(2)}`}
            />
          </Flex>
          <Flex gap="xs">
            <ViewModeButton
              active={!useAllOrders}
              label="Won orders"
              onClick={() => setUseAllOrders(false)}
            />
            <ViewModeButton
              active={useAllOrders}
              label="All orders"
              onClick={() => setUseAllOrders(true)}
            />
          </Flex>
        </Flex>
      )}

      <Flex align="center" gap="md" justify="space-between">
        <Flex gap="xs" wrap="wrap">
          {Object.values(ViewMode)
            .filter(
              (mode) => !isUspsMarketRatesMode || mode !== ViewMode.Orders,
            )
            .map((mode) => (
              <ViewModeButton
                active={viewMode === mode}
                key={mode}
                label={mode}
                onClick={() => onViewModeChange(mode)}
              />
            ))}
        </Flex>
      </Flex>

      {viewMode === ViewMode.Distribution ? (
        <OrderDistributionTable rateGrid={rateGrid} />
      ) : (
        <Flex
          bgColor="secondary"
          borderColor="primary"
          borderWidth="1px"
          className={styles.tableContainer}
          overflow="auto"
          radius="md"
        >
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.tableHeaderWeight}>Weight</th>
                {stats.zones.map((zone, i) => (
                  <th className={styles.tableHeaderZone} key={i}>
                    Z{zone}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allRows.map(({ row, idx, weightBucket }) => (
                <tr key={idx}>
                  <td className={styles.tableRowHeader}>
                    {formatWeightBucket(weightBucket, stats.weightUnit)}
                  </td>
                  {row.map((cell, colIdx) => {
                    const value = getValueForMode(
                      viewMode,
                      cell.merchantRate,
                      cell.carrierRate,
                      cell.quotedRate,
                      cell.savings,
                      cell.spread,
                      cell.orderCount,
                    );
                    const hasMerchantData = cell.orderCount > 0;

                    const bgColor =
                      value !== null
                        ? getHeatColor(
                            value,
                            stats.minVal,
                            stats.maxVal,
                            viewMode,
                          )
                        : "#f3f4f6";

                    const isClickable = hasMerchantData;
                    const cellClassName = [
                      isClickable
                        ? styles.tableCellClickable
                        : styles.tableCellDefault,
                      value !== null
                        ? styles.tableCellWithValue
                        : styles.tableCellEmpty,
                    ].join(" ");

                    return (
                      <td
                        className={cellClassName}
                        key={colIdx}
                        onClick={
                          isClickable
                            ? () =>
                                handleCellClick(
                                  cell,
                                  weightBucket,
                                  stats.weightUnit,
                                )
                            : undefined
                        }
                        style={{ backgroundColor: bgColor }}
                        title={`Orders: ${cell.orderCount}\nMerchant: $${cell.merchantRate?.toFixed(2) || "-"}\n${selectedCarrier}: $${cell.carrierRate?.toFixed(2) || "-"}\nQuoted: $${cell.quotedRate?.toFixed(2) || "-"}\nSavings: $${cell.savings?.toFixed(2) || "-"}\nSpread: $${cell.spread?.toFixed(2) || "-"}`}
                      >
                        {formatCellValue(value, viewMode)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </Flex>
      )}

      <CellDetailModal
        cellLabel={selectedCell?.label ?? ""}
        isOpen={selectedCell !== null}
        onClose={handleCloseModal}
        samples={selectedCell?.samples ?? null}
        totalOrders={selectedCell?.orderCount ?? 0}
      />
    </Flex>
  );
});

function getValueForMode(
  mode: ViewMode,
  merchantRate: number | null,
  carrierRate: number | null,
  quotedRate: number | null,
  savings: number | null,
  spread: number | null,
  orderCount: number,
): number | null {
  switch (mode) {
    case ViewMode.Merchant:
      return merchantRate;
    case ViewMode.Redo:
      return carrierRate;
    case ViewMode.Quoted:
      return quotedRate;
    case ViewMode.Savings:
      return savings;
    case ViewMode.Spread:
      return spread;
    case ViewMode.Orders:
      return orderCount > 0 ? orderCount : null;
    default:
      return null;
  }
}

const StatCard = memo(function StatCard({
  label,
  value,
  textColor = "primary",
}: {
  label: string;
  value: string;
  textColor?: "primary" | "success" | "warning";
}) {
  return (
    <Flex
      bgColor="secondary"
      borderColor="primary"
      borderWidth="1px"
      className={styles.statCard}
      dir="column"
      gap="xs"
      p="sm"
      radius="md"
    >
      <Text fontSize="xs" textColor="secondary">
        {label}
      </Text>
      <Text
        fontSize="md"
        fontWeight="semibold"
        textColor={textColor as "primary"}
      >
        {value}
      </Text>
    </Flex>
  );
});

const ViewModeButton = memo(function ViewModeButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Flex
      className={
        active ? styles.viewModeButtonActive : styles.viewModeButtonInactive
      }
      onClick={onClick}
      p="xs"
      px="sm"
      radius="sm"
    >
      <Text
        fontSize="xs"
        fontWeight={active ? "semibold" : "regular"}
        textColor={active ? "primary" : "secondary"}
      >
        {label}
      </Text>
    </Flex>
  );
});
